import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Book, BookOpen, CheckCircle, Circle } from 'lucide-react';
import api from '../utils/api';
import { useI18n } from '../contexts/I18nContext';

const BibleBook = () => {
  const { courseId, bookId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [bookData, setBookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBookChapters();
  }, [courseId, bookId]);

  const fetchBookChapters = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/bible/courses/${courseId}/books/${bookId}/chapters`);
      setBookData(response.data);
    } catch (err) {
      setError('Failed to load chapters');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openChapter = (chapter) => {
    navigate(`/bible/${courseId}/${bookId}/${chapter}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error || !bookData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-red-400">{error || 'Book not found'}</div>
      </div>
    );
  }

  const { book, chapters } = bookData;
  const isHebrew = courseId === 'biblical_hebrew';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-slate-400 mb-6">
          <Link to="/bible-courses" className="hover:text-amber-400 transition-colors">
            {t('bible.courses')}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{book.name}</span>
        </div>

        {/* Header */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center 
              ${isHebrew ? 'bg-blue-500/20' : 'bg-purple-500/20'}`}>
              <BookOpen className={`w-8 h-8 ${isHebrew ? 'text-blue-400' : 'text-purple-400'}`} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{book.name}</h1>
              {isHebrew && (
                <p className="text-xl text-slate-400 font-hebrew" dir="rtl">{book.hebrew_name}</p>
              )}
              {!isHebrew && book.greek_name && (
                <p className="text-xl text-slate-400">{book.greek_name}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Book className="w-4 h-4" />
              <span>{book.chapters} {t('bible.chapters').toLowerCase()}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span>{chapters.filter(c => c.completed).length} {t('bible.completed')}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                style={{ width: `${(chapters.filter(c => c.completed).length / book.chapters) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Chapter Grid */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">{t('bible.chapters')}</h2>
          
          <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-10 gap-2">
            {chapters.map((chapter) => (
              <button
                key={chapter.chapter}
                data-testid={`chapter-btn-${chapter.chapter}`}
                onClick={() => openChapter(chapter.chapter)}
                className={`relative aspect-square rounded-lg flex items-center justify-center text-lg font-medium transition-all
                  ${chapter.completed 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30' 
                    : 'bg-slate-900/50 text-white border border-slate-700 hover:border-amber-500/50 hover:bg-slate-900/70'}`}
              >
                {chapter.chapter}
                {chapter.completed && (
                  <CheckCircle className="absolute -top-1 -right-1 w-4 h-4 text-green-400 bg-slate-800 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Continue Reading CTA */}
        {chapters.length > 0 && (
          <div className="mt-6 flex justify-center">
            {(() => {
              // Find first incomplete chapter
              const nextChapter = chapters.find(c => !c.completed) || chapters[0];
              return (
                <button
                  data-testid="continue-reading-btn"
                  onClick={() => openChapter(nextChapter.chapter)}
                  className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-lg transition-colors"
                >
                  <span>
                    {chapters.some(c => c.completed) 
                      ? `${t('bible.continueFrom')} ${nextChapter.chapter}` 
                      : t('bible.startReading')}
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default BibleBook;
